/**
 * Funnel events of the skill games (@tg/shared skill.ts SKILL_AN_EVENTS) → POST /api/a/x, cookie-less like the
 * page-view beacon (features/analytics/beacon.ts): no id leaves the device, Do Not Track / GPC / automation and
 * administrators send nothing, the server turns the request into a daily visitor hash and drops it on any doubt.
 *
 *   card    — a skill game's card was seen in the lobby (once per game and page load)
 *   start   — a session started (training / contest / bot / live)
 *   first   — the first action of a session; `v` = ms since the start
 *   hint    — a hint was used
 *   finish  — the session ended solved / the game ended; `v` = seconds played
 *   exit    — the player left an unfinished session; `v` = seconds played
 *   restore — a saved session was resumed (local or server save)
 *   error   — a control error (an impossible move the UI let through, a save that failed)
 */
import { AN_EXCLUDED_ROLES, type SkillAnBeacon } from '@tg/shared';
import { optedOut } from '@/features/analytics/beacon';
import { useSession } from '@/stores/session';

const URL_ = '/api/a/x';
const seenCards = new Set<string>();

/** tests only */
export function __resetSkillBeacon(): void {
  seenCards.clear();
}

export function sendSkillEvent(ev: SkillAnBeacon): void {
  if (typeof navigator === 'undefined' || optedOut()) return;
  const role = useSession.getState().me?.role;
  if (role && (AN_EXCLUDED_ROLES as readonly string[]).includes(role)) return;
  if (ev.e === 'card') {
    if (seenCards.has(ev.g)) return;
    seenCards.add(ev.g);
  }
  const body = JSON.stringify(ev);
  try {
    if (navigator.sendBeacon?.(URL_, new Blob([body], { type: 'text/plain' }))) return;
  } catch {
    /* fall back to fetch */
  }
  void fetch(URL_, {
    method: 'POST',
    body,
    keepalive: true,
    credentials: 'include',
    headers: { 'content-type': 'text/plain', 'x-requested-with': 'tg' },
  }).catch(() => undefined);
}
